/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { colors } from '@apollo/brand';
import { customColors } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { last, range } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useRef } from 'react';
import tinycolor from 'tinycolor2';

import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { date, getTimestampsInRange } from 'src/lib/date/date';
import { format } from 'src/lib/format';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import { Resolution } from 'src/lib/graphqlTypes/types';

import {
  Axis,
  CHAR_HEIGHT,
  useFilteredTicks,
  useHiddenTickIndexes,
} from '../helpers/Axis';
import { usePercentileTicks, useXTimeAxisTicks } from '../helpers/axisTicks';
import { Cursor, useCursorPosition } from '../helpers/Cursor';
import {
  HistogramBucket,
  getBucketAvg,
  getBucketRange,
} from '../helpers/histogram';
import { SvgWrapper, useSvgLayout } from '../helpers/svgLayout';
import { useCursorDateIndex } from '../helpers/useCursorDateIndex';
import { useLinearScale, useTimeScale } from '../helpers/useScale';
import { StackConfig } from '../helpers/useStackInfo';

export type HeatmapStackConfig<Data> = StackConfig<
  Data,
  { timestamp: Timestamp['output']; buckets: HistogramBucket[] }
>;

type StackCount = { value: number; label: string };
type HeatMapDataRecord = {
  timestamp: Date;
  stackCounts: StackCount[];
  index: number;
  count: number;
};

const pixelRatio = 4;
const hueRatio = (r1: number, r2: number) => {
  if (r1 === 0) return 1;
  if (r2 === 0) return 0;
  return r1 / (r1 + r2);
};

export function Heatmap<Data>({
  stacks,
  resolution,
  activeSeries,
  className,
  'data-testid': dataTestId,
  cursorColor = customColors.brand.accent,
}: {
  stacks: HeatmapStackConfig<Data>[];
  resolution: Resolution;
  activeSeries?: boolean[];
  className?: string;
  'data-testid'?: string;
  cursorColor?: string;
}) {
  const cursor = useCursorPosition();
  const { setFirstTick, setMaxYLabelLength, ...svgLayout } = useSvgLayout({});
  const { height, width } = svgLayout;
  const { series, seriesLookup, indexLookup } = useMemo(() => {
    const stackIndexes = range(stacks.length - 1, -1);
    const byIndexLookup: Record<number, HistogramBucket> = {};
    const lookup = Object.values(
      stacks.reduce(
        (
          acc: Record<
            Timestamp['output'],
            { timestamp: Date; indexes: Record<number, HeatMapDataRecord> }
          >,
          { data, getValue },
          stackId,
        ) => {
          if (activeSeries && !activeSeries[stackId]) return acc;
          data.forEach((bucket, bucketIndex, arr) => {
            const { timestamp, buckets } = getValue(bucket);
            const timestampCache = getOrSetRecord(
              timestamp,
              { timestamp: moment(timestamp).toDate(), indexes: {} },
              acc,
            );
            const lastValue = arr[bucketIndex - 1];
            if (lastValue) {
              // The stats schema wasn't returning empty buckets, but we need the 0 data points to properly plot the chart and handle the tooltip.
              // this will fill in the empty buckets. We can remove this if we ensure all stats schema will return every bucket.
              getTimestampsInRange(
                getValue(lastValue).timestamp,
                timestamp,
                resolution,
              ).forEach((zeroTimestamp) => {
                acc[zeroTimestamp] = acc[zeroTimestamp] || {
                  timestamp: moment(zeroTimestamp).toDate(),
                  indexes: {},
                };
              });
            }

            buckets.forEach(({ index, count }) => {
              const byIndex = getOrSetRecord(
                index,
                { index, count },
                byIndexLookup,
              );
              byIndex.count += count;
              const cachedData = getOrSetRecord(
                index,
                {
                  stackCounts: stackIndexes.map(() => ({
                    value: 0,
                    label: '0',
                  })),
                  timestamp: new Date(timestamp),
                  index,
                  count: 0,
                },
                timestampCache.indexes,
              );

              const stackCount = cachedData.stackCounts[stackId];
              if (!stackCount) throw new Error('missing stack');
              stackCount.value += count;
              cachedData.count += count;
              stackCount.label = format.metric(stackCount.value, '0[.]0a');
            });
          });
          return acc;
        },
        {},
      ),
    );
    return {
      stackIds: stackIndexes.filter((i) => !activeSeries || activeSeries[i]),
      series: Object.values(lookup).flatMap((o) => Object.values(o.indexes)),
      seriesLookup: lookup,
      indexLookup: Object.values(byIndexLookup),
    };
  }, [stacks, activeSeries, resolution]);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const {
    minIndex,
    maxIndex,
    minDate,
    maxDate,
    minCount,
    minStackCount,
    maxCount,
    maxStackCount,
  } = useMemo(() => {
    const firstBucket = series[0];
    const values = {
      minIndex: firstBucket?.index ?? 0,
      maxIndex: firstBucket?.index ?? 0,
      minCount: firstBucket?.count ?? 0,
      maxCount: firstBucket?.count ?? 0,
      minStackCount: firstBucket?.stackCounts[0]?.value ?? 0,
      maxStackCount: firstBucket?.stackCounts[0]?.value ?? 0,
      minMoment: moment(firstBucket?.timestamp),
      maxMoment: moment(firstBucket?.timestamp),
    };

    const dateOffset =
      moment(
        date.incrementByResolution(moment().toISOString(), resolution),
      ).diff(moment()) / 2;

    seriesLookup.forEach(({ indexes, timestamp }) => {
      values.minMoment = moment.min(values.minMoment, moment(timestamp));
      values.maxMoment = moment.max(values.maxMoment, moment(timestamp));
      Object.values(indexes).forEach((bucket) => {
        values.maxIndex = Math.max(values.maxIndex, bucket.index);
        values.minIndex = Math.min(values.minIndex, bucket.index);
        values.maxCount = Math.max(values.maxCount, bucket.count);
        values.minCount = Math.min(values.minCount, bucket.count);
        const stackValues = bucket.stackCounts.map(({ value }) => value);
        values.maxStackCount = Math.max(values.maxStackCount, ...stackValues);
        values.minStackCount = Math.min(values.minStackCount, ...stackValues);
      });
    });
    return {
      ...values,
      minDate: values.minMoment.subtract(dateOffset, 'milliseconds').toDate(),
      maxDate: values.maxMoment.add(dateOffset, 'milliseconds').toDate(),
    };
  }, [resolution, series, seriesLookup]);

  const xScale = useTimeScale({
    range: [0, width],
    domain: [minDate, maxDate],
  });

  const yScale = useLinearScale({
    range: [height, 0],
    domain: [minIndex, maxIndex + 1],
  });

  const numIndexBuckets = maxIndex - minIndex + 1;
  const barWidth = width / seriesLookup.length;
  const barHeight = height / numIndexBuckets;
  const cursorYValue = useMemo(() => {
    const indexValue = indexLookup.find(
      (bucket) => bucket.index === cursor?.latencyIndex,
    );
    if (!indexValue) return null;
    return { ...indexValue, y: yScale(maxIndex) };
  }, [cursor?.latencyIndex, indexLookup, maxIndex, yScale]);

  const cursorXValueIndex = useCursorDateIndex({
    data: seriesLookup,
    getTimestamp: (bucket: { timestamp: Date }) => bucket.timestamp,
  });
  const cursorXValue = useMemo(() => {
    if (cursorXValueIndex === null) return null;
    const value = seriesLookup[cursorXValueIndex];
    const timestamp = value?.timestamp;
    if (!timestamp || !value) return null;
    return { timestamp, x: xScale(timestamp), indexes: value.indexes };
  }, [cursorXValueIndex, seriesLookup, xScale]);

  const cursorValue = useMemo(() => {
    if (!cursorYValue || !cursorXValue) return null;
    const { rangeBeginMs, rangeEndMs } = getBucketRange(cursorYValue.index);
    return {
      ...cursorYValue,
      ...cursorXValue,
      tooltipContent: (
        <>
          {(
            Object.values(cursorXValue.indexes).find(
              ({ index }) => index === cursorYValue.index,
            )?.stackCounts ?? stacks.map(() => ({ label: '0' }))
          ).map((value, stackId) => {
            if (activeSeries && !activeSeries[stackId]) return null;
            const { label, unit } = stacks[stackId] ?? {};
            return (
              <strong key={`${value.label}${label}${unit}`} className="block">
                {label ? `${label}: ` : ''}
                {value.label}
                {unit}
              </strong>
            );
          })}
          <div className="text-xs">
            Range: {format.ms(rangeBeginMs, 1, false)} -{' '}
            {format.ms(rangeEndMs, 1, false)}
            <br />
            Time range: <LocalTimestamp value={cursorXValue.timestamp} /> -{' '}
            <LocalTimestamp
              value={date.incrementByResolution(
                cursorXValue.timestamp,
                resolution,
              )}
            />
          </div>
        </>
      ),
    };
  }, [activeSeries, cursorXValue, cursorYValue, resolution, stacks]);

  const {
    ticks: xTicks,
    cursorTick: xCursorTick,
    hiddenTickIndexs,
  } = useXTimeAxisTicks({
    scale: xScale,
    width,
    cursorTimestamp: cursorXValue?.timestamp ?? null,
    firstDate: seriesLookup[0]?.timestamp,
    setFirstTick,
  });

  const yAxisTicks = useFilteredTicks({
    tickValues: usePercentileTicks(indexLookup),
    scale: yScale,
    defaultValue: 0,
    side: 'right',
  });
  const yCursorTick = useMemo(() => {
    return cursorYValue
      ? {
          value: cursorYValue.index,
          labels: [`${format.ms(getBucketAvg(cursorYValue.index), 1, false)}`],
        }
      : undefined;
  }, [cursorYValue]);
  const hiddenYAxisTickIndexs = useHiddenTickIndexes({
    ticks: yAxisTicks,
    scale: yScale,
    cursorTick: yCursorTick,
    side: 'right',
  });
  useEffect(() => {
    setMaxYLabelLength(
      Math.max(
        0,
        ...yAxisTicks.flatMap(({ labels }) => labels.map((l) => l.length)),
      ),
    );
  }, [setMaxYLabelLength, yAxisTicks]);

  // Draw rects
  const hScale = useLinearScale({
    domain: [0, 1],
    range: [
      stacks[0]?.primaryColor ?? colors.primitives.blue[300],
      last(stacks)?.primaryColor ?? customColors.flowchart.a,
    ],
  });
  const sScale = useLinearScale({
    domain: [minCount, maxCount],
    range: [0.6, 0.95],
  });
  const lScale = useLinearScale({
    domain: [minStackCount, maxStackCount],
    range: [0.92, 0.4],
  });

  const barRectWidth = Math.min(Math.max(barWidth - 1, 1), barWidth);
  const barRectHeight = Math.min(Math.max(barHeight - 1, 1), barHeight);
  useEffect(() => {
    if (!canvasRef.current) return;
    canvasRef.current.width = width * pixelRatio;
    canvasRef.current.height = height * pixelRatio;

    const context = canvasRef.current.getContext('2d');
    if (!context) return;
    context.scale(pixelRatio, pixelRatio);

    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    series.forEach(({ stackCounts, count, timestamp, index }) => {
      context.beginPath();
      context.fillStyle = `hsla(${
        tinycolor(
          hScale(
            hueRatio(stackCounts[0]?.value ?? 0, last(stackCounts)?.value ?? 0),
          ),
        ).toHsl().h
      }, ${sScale(count) * 100}%, ${lScale(count) * 100}%, 1)`;
      context.rect(
        xScale(timestamp) - barWidth / 2,
        yScale(index) - barHeight,
        barRectWidth,
        barRectHeight,
      );
      context.fill();
      context.closePath();
    });
  }, [
    barHeight,
    barRectHeight,
    barRectWidth,
    barWidth,
    hScale,
    height,
    lScale,
    sScale,
    series,
    width,
    xScale,
    yScale,
    stacks,
  ]);
  if (maxCount === 0) return null;
  return (
    <SvgWrapper
      className={classnames(className, 'min-h-56')}
      css={css`
        height: ${svgLayout.x + (6 + CHAR_HEIGHT) + numIndexBuckets * 3}px;
      `}
      getCursor={(n) => ({
        date: moment(xScale.invert(n[0])),
        latencyIndex: Math.round(yScale.invert(n[1])),
      })}
      canvasRef={canvasRef}
      data-testid={dataTestId}
      {...svgLayout}
    >
      <Axis
        scale={xScale}
        y={height}
        cursorTick={xCursorTick}
        hiddenTicks={hiddenTickIndexs}
        tickValues={xTicks}
        side="bottom"
        cursorContrastMode="high"
      />
      <Axis
        tickValues={yAxisTicks}
        scale={yScale}
        x={width}
        cursorTick={yCursorTick}
        hideDomain
        hiddenTicks={hiddenYAxisTickIndexs}
        side="right"
      />
      {cursorYValue && (
        <>
          {/* left */}
          <line
            stroke={cursorColor}
            y1={yScale(cursorYValue.index) - (barHeight + 1) / 2}
            y2={yScale(cursorYValue.index) - (barHeight + 1) / 2}
            x2={
              cursorXValue
                ? xScale(cursorXValue.timestamp) - (barWidth + 1) / 2
                : xScale(maxDate)
            }
          />
          {/* right */}
          {cursorXValue && (
            <line
              stroke={cursorColor}
              y1={yScale(cursorYValue.index) - (barHeight + 1) / 2}
              y2={yScale(cursorYValue.index) - (barHeight + 1) / 2}
              x1={xScale(cursorXValue.timestamp) + (barWidth - 1) / 2}
              x2={xScale(maxDate) - 0.5}
            />
          )}
        </>
      )}
      {cursorXValue && (
        <>
          {/* top */}
          <line
            stroke={cursorColor}
            x1={xScale(cursorXValue.timestamp) - 0.5}
            x2={xScale(cursorXValue.timestamp) - 0.5}
            y2={
              cursorYValue
                ? yScale(cursorYValue.index) - barHeight - 0.5
                : yScale(minIndex)
            }
          />
          {/* bottom */}
          {cursorYValue && (
            <line
              stroke={cursorColor}
              x1={xScale(cursorXValue.timestamp) - 0.5}
              x2={xScale(cursorXValue.timestamp) - 0.5}
              y1={yScale(cursorYValue.index) - 0.5}
              y2={yScale(minIndex)}
            />
          )}
        </>
      )}
      {cursorValue && <Cursor cursorValue={cursorValue} width={width} />}
    </SvgWrapper>
  );
}
